import jsonpack from "jsonpack";
import sweetalert from "sweetalert";
import Radio from "backbone.radio";
import { captureException } from "extensions/sentry";
import { toast } from "extensions/toast";

import strings from "common/strings";
import {
    v2,
    user,
    benchmarks
} from "common/config/api";
import { store } from "common/store";

import { request } from "common/components/request";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";
import { pazienHistory } from "common/components/pazienHistory";
import { Getters } from "common/components/getters/getters";

import { getAllPivotsWithValues, findMccCategory } from "common/helpers/dimensionHelpers";

import { defaultBenchmarksState } from "../reducers/benchmarks";

const updateURL = (state, { trigger = false, replace = false } = {}) => {
    const { qualifiers, displayOptions } = STATE_TO_URL_PROPS();
    // Make crazy ass URL string that we SHOULD revise in the future and store references
    if (state.benchmarks.id) {
        pazienHistory.navigateWithParams(`reports/benchmarks/${state.benchmarks.id}/${displayOptions}/${qualifiers}`, { trigger, replace });
    } else {
        pazienHistory.navigateWithParams(`analytics/benchmarks/${displayOptions}/${qualifiers}`, { trigger, replace });
    }
};

export function STATE_TO_URL_PROPS() {
    const state = store.getState();
    
    return {
        qualifiers: window.encodeURIComponent(state.benchmarks.qualifiers),
        displayOptions: window.encodeURIComponent(`metrics_view:${state.benchmarks.metrics_view} expanded_metric:${state.benchmarks.expanded_metric} metrics:${state.benchmarks.metrics.join(",")} segments:${Qualifiers.normalizeValue(jsonpack.pack(state.benchmarks.segments))} active_tab:${state.benchmarks.active_tab} active_tab_structure:${Qualifiers.normalizeValue(jsonpack.pack(state.benchmarks.active_tab_structure))}`)
    };
}

export function DEFAULT_STATE_TO_URL_PROPS() {
    return {
        qualifiers: window.encodeURIComponent(defaultBenchmarksState.qualifiers),
        displayOptions: window.encodeURIComponent(`metrics_view:${defaultBenchmarksState.benchmarks.metrics_view} expanded_metric:${defaultBenchmarksState.expanded_metric} metrics:${defaultBenchmarksState.metrics.join(",")} segments:${Qualifiers.normalizeValue(jsonpack.pack(defaultBenchmarksState.segments))} active_tab:${defaultBenchmarksState.active_tab} active_tab_structure:${Qualifiers.normalizeValue(jsonpack.pack(defaultBenchmarksState.active_tab_structure))}`)
    };
}

export function FETCH_MERCHANT_COUNTS() {
    return (dispatch, getState) => {
        // For each segment that we don't have the count data, we should fetch the count data
        const state = getState();
        
        state.benchmarks.segments.forEach((segment, index) => {
            const hash = `${state.common.date_range.startDate}_${state.common.date_range.endDate}_${segment.filters}`;
    
            if (segment.your || Object.prototype.hasOwnProperty.call(state.benchmarks.merchantCountBySegment, hash)) {
                return;
            }
    
            dispatch({
                type: "FETCH_MERCHANT_COUNT_START",
                hash
            });
            
            // setTimeout(() => {
            //     dispatch({
            //         type: "FETCH_MERCHANT_COUNT_SUCCESS",
            //         data: {
            //             authorization: index > 1 ? 6 : 1
            //         },
            //         hash
            //     });
            // }, 1000);
            
            request(benchmarks.merchant_count, {
                data: {
                    start: state.common.date_range.startDate,
                    end: state.common.date_range.endDate,
                    benchmark: new Qualifiers({
                        value: segment.filters
                    }).toString(true)
                }
            }).then((data) => {
                dispatch({
                    type: "FETCH_MERCHANT_COUNT_SUCCESS",
                    data,
                    hash
                });
            }, () => {
                dispatch({
                    type: "FETCH_MERCHANT_COUNT_ERROR",
                    hash
                });
            });
        });
    };
}

export function makeSegmentUrl({
    segment_filters,
    qualifiers,
    category,
    state,
    your
}, pivot = []) {
    const params = {
        start: state.common.date_range.startDate,
        end: state.common.date_range.endDate,
        datetype: state.common.date_range.datetype,
        currency: state.common.currency
    };
    
    // We put page filters and benchmark filters into "benchmarks" queryparam,
    // and merchant data filters and page filters into "q"
    
    const segmentQ = new Qualifiers({
        value: segment_filters
    });
    segmentQ.addOrReplace(qualifiers);
    
    if (your === true) {
        params.benchmark = void 0;
        params.q = segmentQ.toString(true);
    } else {
        params.benchmark = segmentQ.toString(true);
        
        const yourFilters = new Qualifiers({
            value: your
        });
        yourFilters.addOrReplace(qualifiers);
        params.q = yourFilters.toString(true);
    }
    
    if (pivot[0] === "date_time") {
        params.freq = "month";
    }
    
    return v2[category].byPivot(pivot, params);
}

export function RUN_BENCHMARKS({ dialog, trigger = true }) {
    return (dispatch, getState) => {
        const state = getState();
        
        // Validate input. Make sure each segment has filters
        if (state.benchmarks.benchmarks_configurator.segments.some(({ filters }, i) => !filters && i > 0)) {
            sweetalert({
                title: "Filters are required for all benchmarks",
                text: "",
                icon: "warning",
                buttons: {
                    confirm: {
                        text: strings.gotIt,
                        className: "btn-confirm"
                    },
                    cancel: {
                        visible: false
                    }
                }
            });
            return;
        }
        
        if (typeof state.benchmarks.benchmarks_configurator.index === "undefined") {
            dispatch({
                type: "RUN_BENCHMARKS_FROM_ANALYTICS",
                qualifiers: state.benchmarks.benchmarks_configurator.qualifiers,
                segments: state.benchmarks.benchmarks_configurator.segments,
                metrics: state.benchmarks.benchmarks_configurator.metrics
            });
        } else {
            const existingSegments = state.benchmarks.segments;
            const [newSegment] = state.benchmarks.benchmarks_configurator.segments;
            
            const nextSegments = state.benchmarks.benchmarks_configurator.index === -1 ? [...existingSegments, newSegment]
                : existingSegments.map((s, i) => {
                    // Replace existing segment with the new one
                    if (i === state.benchmarks.benchmarks_configurator.index) {
                        return newSegment;
                    }
                    
                    return s;
                });
            
            dispatch({
                type: "UPDATE_SEGMENTS",
                segments: nextSegments
            });
        }
        
        dialog.dismiss();
        
        updateURL(getState(), { trigger });
    };
}

export function ADD_EMPTY_SEGMENT_TO_CONFIGURATION() {
    return (dispatch, getState) => {
        const state = getState();
        
        dispatch({
            type: "CONFIGURE_BENCHMARKS",
            qualifiers: state.benchmarks.benchmarks_configurator.qualifiers,
            segments: [...state.benchmarks.benchmarks_configurator.segments, {}],
            metrics: state.benchmarks.benchmarks_configurator.metrics,
            index: state.benchmarks.benchmarks_configurator.index
        });
    };
}

export function UPDATE_SEGMENT_CONFIGURATION({ index, name, filters }) {
    return (dispatch, getState) => {
        dispatch({
            type: "UPDATE_SEGMENT_CONFIGURATION",
            index,
            name,
            filters
        });
    };
}

export function UPDATE_METRICS_CONFIGURATION({ metrics }) {
    return (dispatch, getState) => {
        dispatch({
            type: "UPDATE_METRICS_CONFIGURATION",
            metrics
        });
    };
}

export function REMOVE_SEGMENT_FROM_CONFIGURATION({ index }) {
    return (dispatch, getState) => {
        dispatch({
            type: "REMOVE_SEGMENT_FROM_CONFIGURATION",
            index
        });
    };
}

const getNullOrValue = (value) => {
    if (value === "null") {
        return null;
    }
    
    return value;
};

// Right now we take params from the URL and then call action to save them in the store
// Later we should switch to benchmarks ID and resolve it server-side
export function UPDATE_BENCHMARKS_FROM_URL({ qualifiers, displayOptions }) {
    return (dispatch) => {
        const q = new Qualifiers({
            value: displayOptions
        });
        
        const filters = new Qualifiers({
            value: qualifiers
        });
        
        // Do not error when parsing doesn't work for packed values
        let segments;
        try {
            segments = jsonpack.unpack(q.get("segments")[0].replace(/&quot;/g, "\"").replace(/&#x27;/g, "'"));
        } catch {
            segments = [];
        }
        
        let active_tab_structure;
        try {
            active_tab_structure = jsonpack.unpack(q.get("active_tab_structure")[0]);
        } catch {
            active_tab_structure = {};
        }
        
        dispatch({
            type: "UPDATE_BENCHMARKS_FROM_URL",
            qualifiers: filters.toString(),
            segments,
            active_tab: getNullOrValue(q.get("active_tab")[0]),
            active_tab_structure,
            metrics: q.get("metrics"),
            metrics_view: q.get("metrics_view")[0],
            expanded_metric: getNullOrValue(q.get("expanded_metric")[0])
        });
    };
}

export function LOAD_BENCHMARKS_REPORT({ qualifiers, displayOptions, reportId }) {
    return (dispatch) => request(`${user.reports}/${reportId}`).then((data) => {
        const q = new Qualifiers({
            value: displayOptions
        });
        
        let expanded_metric = data.expanded_metric;
        if (q.get("expanded_metric").length) {
            expanded_metric = q.get("expanded_metric")[0];
        }
        
        let metrics = data.metrics;
        if (q.get("metrics").length) {
            metrics = q.get("metrics");
        }
        
        let search_qualifiers = data.qualifiers;
        if (qualifiers) {
            search_qualifiers = qualifiers;
        }
        
        let segments = data.segments;
        if (q.get("segments").length) {
            segments = jsonpack.unpack(q.get("segments")[0]);
        }
        
        dispatch({
            type: "RUN_BENCHMARKS_FROM_REPORT",
            name: data.name,
            id: data._id,
            dates: data.dates,
            datetype: data.datetype,
            expanded_metric,
            metrics,
            pivot_table_structures: data.pivot_table_structures,
            qualifiers: search_qualifiers,
            segments,
            original_report_data: data
        });
        
        return data;
    }, () => {
        toast.error(strings.reports_load_error_message);
    });
}

export function UPDATE_ACTIVE_TAB({ pivot }) {
    return (dispatch, getState) => {
        dispatch({
            type: "UPDATE_ACTIVE_TAB",
            tab: pivot
        });
        
        updateURL(getState(), { replace: true });
    };
}

export function REMOVE_METRIC_CARD({ id }) {
    return (dispatch, getState) => {
        dispatch({
            type: "REMOVE_METRIC_CARD",
            id
        });
        
        updateURL(getState());
    };
}

// export function RUN_WIZARD() {
//     return async(dispatch, getState) => {
//         try {
//             const { showWizard } = await import(
//                 /* webpackChunkName: "benchmarkWizard" */
//                 "merchant/features/analytics/common/benchmarks/metrics/configureMetricsDialogs"
//             );
//
//             showWizard();
//         } catch (err) {
//             ensureErrorCallback(err);
//         }
//     };
// }

export function PRELOAD_BENCHMARK_DATA() {
    return (dispatch, getState) => getAllPivotsWithValues({ filter: (all) => ["mcc"] }).then((pivots) => {
        if (Array.isArray(pivots.mcc) && pivots.mcc.length) {
            const mcc_categories = new Set();
            const mccs = [...pivots.mcc].filter(Boolean);
        
            while (mcc_categories.size < 3 && mccs.length) {
                const category = findMccCategory(mccs.pop(), false);
                if (category) {
                    mcc_categories.add(category);
                }
            }
        
            dispatch({
                type: "ADD_SEGMENT",
                segment: {
                    name: "Peers by MCC category",
                    your: false,
                    filters: `mcc_category:${[...mcc_categories].map(Qualifiers.normalizeValue).join(",")}`
                }
            });
        
            dispatch({
                type: "ADD_SEGMENT",
                segment: {
                    name: "Peers by MCC",
                    your: false,
                    filters: `mcc:${pivots.mcc.filter(Boolean).map(Qualifiers.normalizeValue).join(",")}`
                }
            });
        }
    
        // Add exclude test auths by default
        dispatch({
            type: "BENCHMARK_UPDATE_FILTERS",
            qualifiers: "auths_tests:exclude auths_attempts:initials"
        });
    
        // Then dispatch action to indicate that the page is loaded
        dispatch({
            type: "PAGE_LOADED"
        });
    });
}

export function EXPAND_METRIC_CARD({ id }) {
    return (dispatch, getState) => {
        dispatch({
            type: "EXPAND_METRIC_CARD",
            id
        });
        
        updateURL(getState());
    };
}

export function SWITCH_VIEW_TABLE() {
    return (dispatch, getState) => {
        dispatch({
            type: "SWITCH_VIEW_TABLE"
        });
        
        updateURL(getState());
    };
}

export function SWITCH_VIEW_GRID() {
    return (dispatch, getState) => {
        dispatch({
            type: "SWITCH_VIEW_GRID"
        });
        
        updateURL(getState());
    };
}

export function COLLAPSE_METRIC() {
    return (dispatch, getState) => {
        dispatch({
            type: "COLLAPSE_METRIC"
        });
        
        updateURL(getState());
    };
}

export function REMOVE_SEGMENT({ index }) {
    return (dispatch, getState) => {
        dispatch({
            type: "REMOVE_SEGMENT",
            index
        });
        
        updateURL(getState());
    };
}

export function UPDATE_FILTERS({ qualifiers }) {
    return (dispatch, getState) => {
        dispatch({
            type: "BENCHMARK_UPDATE_FILTERS",
            qualifiers: qualifiers.toString()
        });
        
        updateURL(getState());
    };
}

export function SAVE_AS_BENCHMARKS_REPORT(payload) {
    return request(user.reports, {
        method: "POST",
        data: payload
    }).then((data) => {
        toast.success(strings.reports_save_success_message);
        pazienHistory.navigateWithParams(`reports/benchmarks/${data._id}`, {
            trigger: true
        });
    }, () => {
        toast.error(strings.reports_save_error_message);
    });
}

export function UPDATE_BENCHMARKS_REPORT(dispatch, _store) {
    const state = _store.getState().benchmarks;
    
    const payload = {
        dates: state.dates,
        datetype: state.datetype,
        name: state.name,
        type: "benchmarks",
        qualifiers: state.qualifiers,
        segments: state.segments,
        metrics: state.metrics,
        expanded_metric: state.expanded_metric,
        pivot_table_structures: state.pivot_table_structures
    };
    
    return request(`${user.reports}/${state.id}`, {
        method: "PUT",
        data: payload
    }).then((data) => {
        toast.success(strings.reports_save_success_message);
        dispatch({
            type: "UPDATE_REPORT_ORIGINAL_DATA",
            original_report_data: payload
        });
        pazienHistory.navigateWithParams(`reports/benchmarks/${state.id}`);
    }, () => {
        toast.error(strings.reports_save_error_message);
    });
}

function FETCH_WIDGET_TOTALS(data_hash, { segments, qualifiers, getter }) {
    const yourData = segments.find(({ your }) => your === true);
    
    return (dispatch, getState) => {
        const props = segments.reduce((memo, { filters, your }, index, all) => {
            if (your && segments.length > 1) return memo;
            
            return {
                ...memo,
                [index]: request(makeSegmentUrl({
                    segment_filters: filters,
                    qualifiers,
                    category: Getters.categoryToApiResource[getter.category],
                    state: getState(),
                    your: your || yourData.filters
                }))
            };
        }, {});
        
        dispatch({
            type: "FETCH_WIDGET_START",
            hash: data_hash
        });
        
        return Promise.props(props).then((data) => {
            if (segments.length > 1 && yourData) {
                data = {
                    "0": data[1],
                    ...data
                };
            }
            
            // Run success reducer to populate the store
            dispatch({
                type: "FETCH_WIDGET_SUCCESS",
                hash: data_hash,
                data
            });
        }, (error) => {
            // Run error reducer to show the error
            dispatch({
                type: "FETCH_WIDGET_ERROR",
                hash: data_hash,
                error
            });
        });
    };
}

function shouldFetchWidget(data_hash) {
    // Here we check if we already have the data for the requested widget OR if fetch is on progress for the requested widget
    const state = store.getState();
    if (state.benchmarks.widgets[data_hash] && state.benchmarks.widgets[data_hash].status === "fetched") {
        return false;
    }
    return true;
}

function FETCH_WIDGET_TIMELINE(data_hash, { segments, qualifiers, getter }) {
    const yourData = segments.find(({ your }) => your === true);
    
    return (dispatch, getState) => {
        const props = segments.reduce((memo, { filters, your }, index) => {
            if (your && segments.length > 1) return memo;
            
            return {
                ...memo,
                [index]: request(makeSegmentUrl({
                    segment_filters: filters,
                    qualifiers,
                    category: Getters.categoryToApiResource[getter.category],
                    state: getState(),
                    your: your || yourData.filters
                }, ["date_time"]))
            };
        }, {});
        
        dispatch({
            type: "FETCH_WIDGET_START",
            hash: data_hash
        });
        
        return Promise.props(props).then((data) => {
            // Run success reducer to populate the store
            if (segments.length > 1 && yourData) {
                data = {
                    "0": data[1],
                    ...data
                };
            }
            
            dispatch({
                type: "FETCH_WIDGET_SUCCESS",
                hash: data_hash,
                data
            });
        }, (error) => {
            // Run error reducer to show the error
            dispatch({
                type: "FETCH_WIDGET_ERROR",
                hash: data_hash,
                error
            });
        });
    };
}

export function FETCH_WIDGET_TOTALS_IF_NEEDED(data_hash, { segments, qualifiers, getter }) {
    if (shouldFetchWidget(data_hash)) {
        // Run action to request data for the widget
        return FETCH_WIDGET_TOTALS(data_hash, { segments, qualifiers, getter });
    }
    
    // We have the data ready for the widget
    return (dispatch, getState) => Promise.resolve(true);
}

export function FETCH_WIDGET_TIMELINE_IF_NEEDED(data_hash, { segments, qualifiers, getter }) {
    if (shouldFetchWidget(data_hash)) {
        // Run action to request data for the widget
        return FETCH_WIDGET_TIMELINE(data_hash, { segments, qualifiers, getter });
    }
    
    // We have the data ready for the widget
    return (dispatch, getState) => Promise.resolve(true);
}

export function UPDATE_PIVOT_TABLE_FIELDS({ hash, data }) {
    return (dispatch, getState) => {
        dispatch({
            type: "UPDATE_PIVOT_TABLE_FIELDS",
            data: data.newStructure,
            hash
        });
        
        updateURL(getState());
    };
}

export function FETCH_PIVOT_TABLE_DATA({ segments, qualifiers, getters, pivots }) {
    const yourData = segments.find(({ your }) => your === true);
    
    return (dispatch, getState) => {
        // We should group getters by api category and issue as many requests as there are categories
        // Also find all getter keys for benchmark and weighted dependencies
        const { categories, benchmark_keys, weighted_keys, metric_keys } = getters.reduce((memo, { category, key, benchmark_deps = {}, weighted_deps = [] }) => {
            memo.categories.add(Getters.categoryToApiResource[category]);
            Object.values(benchmark_deps).forEach((id) => {
                const getter = Radio.channel("main").request("get:getters").findById(id);
    
                if (getter) {
                    memo.benchmark_keys.add(getter.key);
                }
            });
    
            weighted_deps.forEach((id) => {
                const getter = Radio.channel("main").request("get:getters").findById(id);
        
                if (getter) {
                    memo.weighted_keys.add(getter.key);
                }
            });
            
            memo.metric_keys.add(key);
            
            return memo;
        }, {
            categories: new Set(),
            benchmark_keys: new Set(),
            weighted_keys: new Set(),
            metric_keys: new Set()
        });
        
        const props = segments.reduce((memo, { filters, your }, index, all) => {
            if (your && all.length > 1) return memo;
            
            return {
                ...memo,
                ...[...categories].reduce((memo2, cat) => {
                    try {
                        // Check if we can make a URL for this combination of pivots
                        return {
                            ...memo2,
                            [`${index}_${cat}`]: request(makeSegmentUrl({
                                segment_filters: filters,
                                qualifiers,
                                category: cat,
                                state: getState(),
                                your: your || yourData.filters
                            }, pivots))
                        };
                    } catch (error) {
                        captureException(error);
                        // If URL can't be built for the combination of pivot then there is no need to waste API resources
                        return memo2;
                    }
                }, {})
            };
        }, {});
        
        const keys = pivots.map((key) => Radio.channel("main").request("get:getters").findPivotKey(key));
        
        return Promise.props(props).then((allSegmentsCategoriesData) => Object.entries(allSegmentsCategoriesData).reduce((memo, [name, data]) => {
            const segment_index = Number.parseInt(name.split("_")[0], 10);
            
            // We need to merge data from different categories by segment and pivots
            return {
                ...memo,
                [segment_index]: [...memo[segment_index] || [], ...data].reduce((memo2, cur) => {
                    const match = memo2.find((y) => keys.every((k) => y[k] === cur[k]));
                    if (match) {
                        Object.assign(match, cur);
                    } else {
                        // Special case to handle when api returns "currency", not "currency_alias" as a key
                        if (cur.currency) cur.currency_alias = cur.currency;
                        memo2.push(cur);
                    }
                    
                    return memo2;
                }, [])
            };
        }, {})).then((data) => {
            // If we saved a request then use first benchmark item instead of your data
            if (yourData && data[1]) {
                return {
                    // We should set "N/A" for any benchmark_dep in this object
                    "0": data[1].map((v) => Object.entries(v).reduce((memo, [key, value]) => {
                        if (benchmark_keys.has(key)) {
                            memo[key] = "N/A";
                        } else {
                            memo[key] = value;
                        }
        
                        return memo;
                    }, {})),
    
                    // Set N/A for all weighted values in their data and for the the metric key itself
                    ...Object.entries(data).reduce((memo, [index, values]) => ({
                        ...memo,
                        [index]: values.map((v) => Object.entries(v).reduce((val_memo, [key, value]) => {
                            if (weighted_keys.has(key) || metric_keys.has(key)) {
                                val_memo[key] = "N/A";
                            } else {
                                val_memo[key] = value;
                            }
            
                            return val_memo;
                        }, {}))
                    }), {})
                };
            }
            
            return data;
        });
    };
}
